import React, { useCallback, useEffect, useState } from "react";
import { History, RefreshCw, UserCheck, Users } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Skeleton } from "./ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";
import { api, errMsg, fmtINR } from "../lib/api";
import { useStaffLabel } from "./StaffAvatar";
import { monthsInRange, isFullMonths } from "../lib/dateRange";

// Per design tokens: primary (deep blue) for the supervisor's own target, success green for the team goal.
const COLOR_PERSONAL = "hsl(206, 88%, 28%)";
const COLOR_GOAL = "hsl(152, 55%, 32%)";

/** Status label for a month row: closed months read Achieved / Missed, the current month uses pacing. */
export const historyStatus = (status, period) => {
  if (period === "future") return { label: "Upcoming", cls: "bg-muted text-muted-foreground" };
  if (status === "achieved") return { label: "Achieved", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" };
  if (status === "on_track") return { label: "On track", cls: "bg-sky-100 text-sky-800 border-sky-200" };
  if (status === "behind") {
    return period === "past"
      ? { label: "Missed", cls: "bg-rose-100 text-rose-800 border-rose-200" }
      : { label: "Behind pace", cls: "bg-amber-100 text-amber-800 border-amber-200" };
  }
  return { label: "No target", cls: "bg-muted text-muted-foreground" };
};

const pct = (v) => (v == null ? "—" : `${v}%`);

function HistoryTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const r = payload[0].payload;
  return (
    <div className="rounded-md border bg-popover/95 backdrop-blur px-3 py-2 text-xs shadow-md space-y-1" data-testid="target-history-tooltip">
      <p className="font-medium">{r.label}</p>
      <p><span className="inline-block h-2 w-2 rounded-sm mr-1.5" style={{ background: COLOR_PERSONAL }} />
        Personal: {r.personal_target ? `${fmtINR(r.personal_collected)} / ${fmtINR(r.personal_target)} (${pct(r.personal)})` : "no target"}</p>
      <p><span className="inline-block h-2 w-2 rounded-sm mr-1.5" style={{ background: COLOR_GOAL }} />
        Team goal: {r.team_goal ? `${fmtINR(r.team_collected)} / ${fmtINR(r.team_goal)} (${pct(r.goal)})` : "no target"}</p>
    </div>
  );
}

function SummaryChip({ icon: Icon, title, s, color, labelOf = (m) => m, testId }) {
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2 min-w-0" data-testid={testId}>
      <p className="text-[11px] text-muted-foreground flex items-center gap-1">
        <Icon className="h-3 w-3" style={{ color }} /> {title}
      </p>
      {s.months_with_target ? (
        <p className="text-xs mt-0.5">
          <span className="font-semibold tabular-nums">{s.months_achieved}</span> achieved
          {s.months_missed > 0 && <> · <span className="font-semibold tabular-nums">{s.months_missed}</span> missed</>}
          {" "}· avg <span className="font-semibold tabular-nums">{pct(s.avg_achievement_pct)}</span>
          {s.best_month && <span className="text-muted-foreground"> · best {labelOf(s.best_month)} ({pct(s.best_pct)})</span>}
        </p>
      ) : (
        <p className="text-xs mt-0.5 text-muted-foreground">No target set in this range</p>
      )}
    </div>
  );
}

/**
 * Month-by-month Personal target vs Team goal achievement for one supervisor.
 * Supervisors see their own history (omit supervisorId); admins pass supervisorId.
 * `endMonth` anchors the 6 / 12-month views (defaults to the current month).
 * Phase 175: `range` = {from, to} adds a "Selected range" view — one row per month touched by the range, partial
 * months pro-rated so the rows add up to the Supervisor Targets tab for the same range.
 */
export function TargetHistoryCard({ code, endMonth, supervisorId, range, title = "Target history" }) {
  const rangeIsMulti = !!(range?.from && range?.to) && (monthsInRange(range).length > 1 || !isFullMonths(range));
  const [mode, setMode] = useState(rangeIsMulti ? "range" : "6");
  const [data, setData] = useState(null);
  const supLabel = useStaffLabel(code, { userId: supervisorId, name: data?.supervisor_name });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const months = mode === "range" ? null : Number(mode);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (mode === "range" && range?.from && range?.to) { params.from = range.from; params.to = range.to; }
      else { params.months = months || 6; if (endMonth) params.end = endMonth; }
      if (supervisorId) params.supervisor_id = supervisorId;
      const r = await api.get(`/t/${code}/supervisor-targets/history`, { params });
      setData(r.data);
    } catch (e) { setError(errMsg(e)); } finally { setLoading(false); }
  }, [code, mode, months, endMonth, supervisorId, range?.from, range?.to]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const chart = rows.map((r) => ({
    ...r,
    personal: r.personal_target ? r.personal_achievement_pct : null,
    goal: r.team_goal ? r.team_goal_achievement_pct : null,
  }));
  const hasAny = rows.some((r) => r.personal_target || r.team_goal);
  const maxPct = Math.max(100, ...chart.map((c) => Math.max(c.personal || 0, c.goal || 0)));
  const yMax = Math.ceil(maxPct / 25) * 25;
  const yTicks = Array.from({ length: yMax / 25 + 1 }, (_, i) => i * 25);
  const labelOf = (m) => rows.find((r) => r.month === m)?.label || m;

  return (
    <Card className="shadow-sm" data-testid="target-history-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-medium flex items-center gap-2"><History className="h-4 w-4" /> {title}</p>
            <p className="text-xs text-muted-foreground">
              Achievement % per month — Personal target vs Team goal{data?.supervisor_name && supervisorId ? ` • ${supLabel}` : ""}
              {rows.length ? ` • ${rows[0].label} to ${rows[rows.length - 1].label}` : ""}
              {mode === "range" && data?.range ? ` • ${data.range.label} (partial months pro-rated by days)` : ""}
            </p>
          </div>
          <ToggleGroup type="single" value={mode} onValueChange={(v) => v && setMode(v)}
            variant="outline" size="sm" data-testid="target-history-range">
            {rangeIsMulti && <ToggleGroupItem value="range" className="text-xs px-3" data-testid="target-history-range-selected">Selected range</ToggleGroupItem>}
            <ToggleGroupItem value="6" className="text-xs px-3" data-testid="target-history-range-6">6 months</ToggleGroupItem>
            <ToggleGroupItem value="12" className="text-xs px-3" data-testid="target-history-range-12">12 months</ToggleGroupItem>
          </ToggleGroup>
        </div>

        {loading && !data ? (
          <div className="space-y-3" data-testid="target-history-loading">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2"><Skeleton className="h-12" /><Skeleton className="h-12" /></div>
            <Skeleton className="h-52 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : error ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-4 text-sm flex items-center justify-between gap-3" data-testid="target-history-error">
            <span>Could not load target history: {error}</span>
            <Button variant="outline" size="sm" onClick={load} data-testid="target-history-retry"><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry</Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <SummaryChip icon={UserCheck} title="Personal target" s={data.summary.personal} color={COLOR_PERSONAL} labelOf={labelOf} testId="target-history-summary-personal" />
              <SummaryChip icon={Users} title="Team goal" s={data.summary.team_goal} color={COLOR_GOAL} labelOf={labelOf} testId="target-history-summary-goal" />
            </div>

            {!hasAny ? (
              <div className="rounded-md border border-dashed px-3 py-8 text-center text-sm text-muted-foreground" data-testid="target-history-empty">
                No Personal target or Team goal was set {mode === "range" ? "in the selected range" : `in the last ${months} months`}. Once the Admin sets targets, the monthly trend appears here.
              </div>
            ) : (
              <div className={`h-56 w-full ${loading ? "opacity-60" : ""}`} data-testid="target-history-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chart} margin={{ top: 8, right: 8, left: -12, bottom: 0 }} barGap={2} barCategoryGap="28%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ef" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} unit="%" domain={[0, yMax]} ticks={yTicks} />
                    <Tooltip content={<HistoryTooltip />} cursor={{ fill: "hsl(206, 30%, 90%)", fillOpacity: 0.35 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="square" iconSize={9} />
                    <ReferenceLine y={100} stroke="hsl(215, 15%, 55%)" strokeDasharray="4 4" label={{ value: "100%", position: "insideTopRight", fontSize: 10, fill: "hsl(215, 15%, 45%)" }} />
                    <Bar dataKey="personal" name="Personal target" fill={COLOR_PERSONAL} radius={[4, 4, 0, 0]} maxBarSize={36} minPointSize={3} />
                    <Bar dataKey="goal" name="Team goal" fill={COLOR_GOAL} radius={[4, 4, 0, 0]} maxBarSize={36} minPointSize={3} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="overflow-x-auto rounded-md border">
              <Table data-testid="target-history-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Month</TableHead>
                    <TableHead className="text-xs">Personal target</TableHead>
                    <TableHead className="text-xs">Team goal</TableHead>
                    <TableHead className="text-xs text-right">Team target</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...rows].reverse().map((r) => {
                    const ps = historyStatus(r.personal_status, r.period);
                    const gs = historyStatus(r.team_goal_status, r.period);
                    return (
                      <TableRow key={r.month} className={r.period === "current" ? "bg-primary/[0.04]" : ""} data-testid={`target-history-row-${r.month}`}>
                        <TableCell className="py-2 text-xs font-medium whitespace-nowrap">
                          {r.label}{r.period === "current" && <span className="ml-1.5 text-[10px] text-muted-foreground font-normal">(this month)</span>}
                          {r.partial && <span className="ml-1.5 text-[10px] text-muted-foreground font-normal" title={`${r.days_in_range} of ${r.days_in_month} days inside the range — target pro-rated`}>· {r.days_in_range}/{r.days_in_month} days</span>}
                        </TableCell>
                        <TableCell className="py-2 text-xs">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="tabular-nums">{r.personal_target ? `${fmtINR(r.personal_collected)} / ${fmtINR(r.personal_target)}` : "—"}</span>
                            {r.personal_target ? <span className="tabular-nums font-semibold">{pct(r.personal_achievement_pct)}</span> : null}
                            <Badge variant="outline" className={`text-[10px] ${ps.cls}`}>{ps.label}</Badge>
                          </div>
                        </TableCell>
                        <TableCell className="py-2 text-xs">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="tabular-nums">{r.team_goal ? `${fmtINR(r.team_collected)} / ${fmtINR(r.team_goal)}` : "—"}</span>
                            {r.team_goal ? <span className="tabular-nums font-semibold">{pct(r.team_goal_achievement_pct)}</span> : null}
                            <Badge variant="outline" className={`text-[10px] ${gs.cls}`}>{gs.label}</Badge>
                          </div>
                        </TableCell>
                        <TableCell className="py-2 text-xs text-right tabular-nums whitespace-nowrap">
                          {r.team_target ? `${fmtINR(r.team_target)}${r.team_target_achievement_pct != null ? ` · ${r.team_target_achievement_pct}%` : ""}` : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
